<template>
  <div>
    <panel-column-layout
      v-if="shouldShow('fontWeight') || shouldShow('color')"
      columns="2"
    >
      <template slot="first">
        <div
          v-if="shouldShow('fontWeight')"
          class="flex flex-col flex-1 flex-grow"
          data-cy="textWeightDropDown"
        >
          <div class="text-xs font-bold text-secondary-invert">
            {{ $t('frontend_builder_text_section_weight') }}
          </div>
          <gd-dropdown
            v-model="properties.fontWeight"
            :options="fontWeightChanger"
            variant="dark"
            size="full"
          />
        </div>
      </template>
      <template slot="second">
        <div
          v-if="shouldShow('color')"
          class="flex flex-col flex-1 flex-grow"
          data-cy="textColorPicker"
        >
          <div class="text-xs font-bold text-secondary-invert">
            {{ $t('frontend_builder_border_section_color') }}
          </div>
          <color-picker
            v-model="properties.color"
            :colors="options.color"
          ></color-picker>
        </div>
      </template>
    </panel-column-layout>

    <gd-separator />

    <font-selector
      v-if="shouldShow('fontFamily')"
      v-model="fontFamily"
      :fontData="options.fontData"
    />
    <gd-separator />

    <div v-if="shouldShow('fontSize')" class="flex flex-col">
      <div class="text-xs font-bold text-secondary-invert">
        {{ $t('frontend_builder_text_section_size') }}
      </div>
      <gd-slider
        v-model="properties.fontSize"
        size="full"
        :min="1"
        :max="100"
        :readonly="false"
        step="1"
        data-cy="textSizeSlider"
      />
    </div>

    <gd-separator />

    <sides-panel
      v-if="shouldShow('textAlign')"
      v-model="properties.textAlign"
      data-cy="textAlignOptions"
      :sides="options.textAlign"
    ></sides-panel>

    <gd-separator />

    <sides-panel
      v-if="shouldShow('styleDecoration')"
      v-model="styleDecoration"
      data-cy="styleDecorationOptions"
      :sides="options.styleDecoration"
      :multi="true"
      :options="textStyles"
    ></sides-panel>

    <gd-separator />

    <div class="flex flex-row">
      <div
        v-if="shouldShow('letterSpacing')"
        class="flex flex-col w-1/2 mr-1"
      >
        <div class="text-xs font-bold text-secondary-invert">
          {{ $t('frontend_builder_text_section_letter_spacing') }}
        </div>
        <gd-dropdown
          v-model="properties.letterSpacing"
          :options="dropdownOptions(options.letterSpacing)"
          data-cy="letterSpacingOptions"
          variant="dark"
          size="full"
        />
      </div>
      <div
        v-if="shouldShow('lineHeight')"
        class="flex flex-col w-1/2 ml-1"
      >
        <div class="text-xs font-bold text-secondary-invert">
          {{ $t('frontend_builder_text_section_letter_height') }}
        </div>
        <gd-dropdown
          v-model="properties.lineHeight"
          :options="dropdownOptions(options.lineHeight)"
          data-cy="textLineHeight"
          variant="dark"
          size="full"
        />
      </div>
    </div>

    <gd-separator />

    <panel-column-layout
      v-if="shouldShow('textStrokeWidth') || shouldShow('textStrokeColor')"
      columns="2"
    >
      <template slot="first">
        <div
          v-if="shouldShow('textStrokeWidth')"
          class="flex flex-col flex-1 flex-grow"
          data-cy="textStrokeWidthDropDown"
        >
          <div class="text-xs font-bold text-secondary-invert">
            {{ $t('frontend_builder_text_section_stroke_width') }}
          </div>
          <gd-dropdown
            v-model="properties.textStrokeWidth"
            :options="dropdownOptions(options.textStrokeWidth)"
            variant="dark"
            size="full"
          />
        </div>
      </template>
      <template slot="second">
        <div
          v-if="shouldShow('textStrokeColor')"
          class="flex flex-col flex-1 flex-grow"
          data-cy="strokeColorPicker"
        >
          <div class="text-xs font-bold text-secondary-invert">
            {{ $t('frontend_builder_text_section_stroke_color') }}
          </div>
          <color-picker
            v-model="properties.textStrokeColor"
            :colors="options.color"
          ></color-picker>
        </div>
      </template>
    </panel-column-layout>

    <gd-separator />

    <div v-if="shouldShow('textShadow')">
      <div class="text-xs font-bold text-secondary-invert">{{ $t('frontend_builder_text_shadow') }}</div>
<!--
    <div class="flex flex-row">
      <div class="flex-grow flex-1 pb-2 flex items-center">
        <gd-dropdown
          v-model="textShadow"
          placeholder="Text Shadow"
          :options="dropdownOptions(options.textShadow)"
          variant="dark"
          size="full"
        />
      </div>
    </div>
-->
    <div>
      <div class="w-full h-8 my-2 flex items-center justify-between">
        <div class="text-xs font-bold text-secondary-invert">{{ $t('frontend_builder_text_section_x_offset') }}</div>
        <div
          class="w-12 h-8 flex items-center justify-center text-center text-sm rounded border-solid border-secondary-light cursor-pointer select-none focus:outline-none"
          :class="{
              'border bg-secondary text-secondary-invert': !isSelected('text', 'x'),
              'bg-primary text-primary-invert': isSelected('text', 'x'),
          }"
          @click="select('text', 'x')"
        >
          {{ getShadowValue('text', 'x') }}
        </div>

        <div class="text-xs font-bold text-secondary-invert">{{ $t('frontend_builder_text_section_y_offset') }}</div>
        <div
          class="w-12 h-8 flex items-center justify-center text-center text-sm rounded border-solid border-secondary-light cursor-pointer select-none focus:outline-none"
          :class="{
              'border bg-secondary text-secondary-invert': !isSelected('text', 'y'),
              'bg-primary text-primary-invert': isSelected('text', 'y'),
          }"
          @click="select('text', 'y')"
        >
          {{ getShadowValue('text', 'y') }}
        </div>
      </div>

      <div class="w-full h-8 my-2 flex">
        <div class="w-6/12 flex items-center">
          <div class="text-xs font-bold text-secondary-invert pr-10">{{ $t('frontend_builder_text_section_blur') }}</div>
          <div
            class="w-12 h-8 flex items-center justify-center text-center text-sm rounded border-solid border-secondary-light cursor-pointer select-none focus:outline-none"
            :class="{
                'border bg-secondary text-secondary-invert': !isSelected('text', 'blur'),
                'bg-primary text-primary-invert': isSelected('text', 'blur'),
            }"
            @click="select('text', 'blur')"
          >
            {{ getShadowValue('text', 'blur') }}
          </div>
        </div>

        <div class="w-6/12 flex items-center">
          <div class="text-xs font-bold text-secondary-invert px-2">{{ $t('frontend_builder_border_section_color') }}</div>
          <color-picker
            v-model="text.color"
            :colors="options.color"
          ></color-picker>
        </div>
      </div>

      <div v-if="type==='text' && selected" class="my-2">
        <gd-slider
          v-model="slider"
          :min="min"
          :max="max"
          :readonly="false"
        />
      </div>

    </div>
  </div>
  </div>
</template>
<script>
import GdDropdown from '@groovepages/gd-ui-kit/components/Dropdown';
import GdSlider from '@groovepages/gd-ui-kit/components/Slider';
import ColorPicker from '@groovepages/gd-ui-kit/components/ColorPicker'
import SidesPanel from '@groovepages/gd-ui-kit/components/SidesSelectorInput';
import GdSeparator from '@groovepages/gd-ui-kit/components/Separator';
import PanelColumnLayout from './PanelColumnLayout';
import tailwindConfig from '@groovepages/gp-page-ui-kit/tailwind';
import Italic from '@fortawesome/fontawesome-free/svgs/solid/italic.svg';
import Overline from '@groovepages/gd-ui-kit/assets/overline.svg';
import Underline from '@fortawesome/fontawesome-free/svgs/solid/underline.svg';
import LineThrough from '@groovepages/gd-ui-kit/assets/lineThrough.svg';
import AlignLeft from '@fortawesome/fontawesome-free/svgs/solid/align-left.svg';
import AlignRight from '@fortawesome/fontawesome-free/svgs/solid/align-right.svg';
import AlignCenter from '@fortawesome/fontawesome-free/svgs/solid/align-center.svg';
import AlignJustify from '@fortawesome/fontawesome-free/svgs/solid/align-justify.svg';
import FontSelector from './FontSelector.vue';

export default {
  props: {
    styles: {
      required: true,
    },
    fontData: {
      required: true,
    }
  },
  components: {
    FontSelector,
    PanelColumnLayout,
    GdDropdown,
    GdSlider,
    ColorPicker,
    SidesPanel,
    GdSeparator
  },
  data() {
    return {
      properties: this.styles,
      options: {
        fontWeight: tailwindConfig.fontWeights,
        letterSpacing: tailwindConfig.tracking,
        lineHeight: tailwindConfig.leading,
        textStrokeWidth: {
          'None': '0',
          '1 px': '1px',
          '2 px': '2px',
          '3 px': '3px',
          '4 px': '4px',
          '5 px': '5px',
        },
        textShadow: tailwindConfig.shadows,
        fontData: this.fontData,
        textAlign: {
          left: AlignLeft,
          center: AlignCenter,
          right: AlignRight,
          justify: AlignJustify
        },
        styleDecoration: {
          italic: Italic,
          overline: Overline,
          underline: Underline,
          'lineThrough': LineThrough,
        }
      },
      min: 0,
      max: 30,
      type: '',
      selected: '',
      text: {
        x: 0,
        y: 0,
        blur: 0,
        color: 'rgba(0, 0, 0, 0.1)'
      },
      weight: {
        '100': 'Hairline',
        '200': 'Extralight',
        '300': 'Light',
        '400': 'Regular',
        '500': 'Medium',
        '600': 'Semibold',
        '700': 'Bold',
        '800': 'Extrabold',
        '900': 'Black'
      },
      textStyles: {
        'italic': false,
        'overline': false,
        'underline': false,
        'lineThrough': false,
      }
    };
  },
  mounted(){
    this.getProperty('text', this.properties.textShadow);
  },
  computed: {
    color: {
      get() {
        return "#555";
      },
      set(value) {
        //
      },
    },
    textAlign: {
      get() {
        return 'left';
      },
      set(value) {
        //
      },
    },
    lineHeight: {
      get() {
        return this.getValue('lineHeight') || 'null';
      },
      set(value) {
        this.setStyle('lineHeight', value !== 'null' ? value : null);
      },
    },
    letterSpacing: {
      get() {
        return this.getValue('letterSpacing') || 'null';
      },
      set(value) {
        this.setStyle('letterSpacing', value !== 'null' ? value : null);
      },
    },
    fontFamily: {
      get() {
        return this.properties.fontFamily;
      },
      set(value) {
        this.properties.fontFamily = value.name;
      },
    },
    textShouldShow() {
      return this.show;
    },
    fontWeightChanger() {
      const fontObj = Object.values(this.options.fontData).find(
        (value) => value.name === this.fontFamily
      );
      let options = {};
      if(typeof fontObj === 'undefined') {
        options = this.options.fontWeight;
      }
      else {
        fontObj.weight.map((wt, index) => {
          if(wt === "regular") {
            options[this.weight["400"]] = 400;
          }
          else if(isFinite(wt)) {
            options[this.weight[wt]] = parseInt(wt);
          }
        });
      }
      return this.dropdownOptions(options);
    },
    slider: {
      get() {
        return parseInt(this[this.type][this.selected]);
      },
      set(value) {
        this[this.type][this.selected] = `${value}px`;
      }
    },
    textShadow: {
      get() {
        return  this.getValue('textShadow');
      },
      set(value) {
        if(value === 'custom') {
          this.getProperty('text', this.getValue('textShadow'));
          return;
        }
        this.setStyle('textShadow', value);
      }
    },
    styleDecoration: {
      get() {
        this.textStyles = {
          italic: this.getValue('fontStyle')?.includes('italic') || false,
          overline: this.getValue('textDecorationLine')?.includes('overline') || false,
          underline: this.getValue('textDecorationLine')?.includes('underline') || false,
          lineThrough: this.getValue('textDecorationLine')?.includes('line-through') || false
        };

      },
      set(value) {
        switch(value) {
          case 'italic':
            this.textStyles.italic = !this.textStyles.italic;
            break;
          case 'overline':
            this.textStyles.overline = !this.textStyles.overline;
            break;
          case 'underline':
            this.textStyles.underline = !this.textStyles.underline;
            break;
          case 'lineThrough':
            this.textStyles.lineThrough = !this.textStyles.lineThrough;
            break;
        }
        this.properties.fontStyle = this.textStyles.italic ? 'italic' : null;
        this.properties.textDecorationLine = `${this.textStyles.overline ? 'overline ' : ''}${this.textStyles.underline ? 'underline ' : ''}${this.textStyles.lineThrough ? 'line-through ' : ''}`;
      }
    }
  },
  watch: {
    properties: {
      deep: true,
      handler(){
        this.$emit('change', this.properties);
      }
    },
    text: {
      deep: true,
      handler(val) {
        const { x, y, blur, color } = val;
        this.properties.textShadow = `${color} ${x} ${y} ${blur}`;
      }
    },
  },
  methods: {
    dropdownOptions(property) {
      return Object.keys(property).map(text => {
        return { text, value: property[text] };
      });
    },
    getValue(el) {
      return '12';
    },
    shouldShow(el) {
      return true;
    },
    /* open() {
      if (this.$refs.panel.toggle && !this.$refs.panel.open) {
        this.$refs.panel.toggle();
      }
    }, */
    formatSize() {
      if (this.getValue('fontSize') === null) return '-';
      return this.getValue('fontSize');
    },
    select(type, value) {
      this.type = type;
      this.selected = value;
    },
    isSelected(type, value) {
      if(this.type === type && this.selected === value) {
        return true;
      }
      return false;
    },
    getShadowValue(type, value) {
      return this[type][value];
    },
    getProperty(type , value) {
      if(!value) return;

      const rgba = value.substring(value.indexOf('rgba('), value.indexOf(')')+1);

      value = value.replace(rgba, '');
      value = value.trim();
      value = value.replace('  ', ' ');
      value = value.split(' ');

      if(type==='text' && value) {
        this.text.x = value[0];
        this.text.y =  value[1];
        this.text.blur = value[2];
        this.text.color = rgba;
      }
    },
  },
};
</script>
